import React, { useState } from 'react';
import Button from "react-bootstrap/Button";
import Card from 'react-bootstrap/Card';
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Stack from "react-bootstrap/Stack";
import { Person } from "react-bootstrap-icons";
import { Badge } from "react-bootstrap";

import { useForm } from "../../hooks/useForm";
import { formatDate, formatMoney } from "../../utilities/common";
import useLongPress from "../../hooks/useLongPress";
import { DeleteButton } from "../../components/DeleteButton";
import { useNavigate } from "react-router-dom";
import { useHouseState, useHouseDispatch } from '../../contexts/HouseContext';

import './House.css'

const House = () => {
    const houseState = useHouseState();
    const { house, games } = houseState;

    const { addGame } = useHouseDispatch();
    const { handleSubmit, status, message } = useForm(addGame, () => { })

    if (!house) {
        return <div>Loading...</div>;
    }

    return (
        <main className='house'>
            <GameListHeader house={house} status={status} handleSubmit={handleSubmit} message={message} />
            <Row className="mt-3 d-flex justify-content-center">
                <Col md={10}>
                    <GameList games={games} />
                </Col>
            </Row>
        </main>
    );
};

const GameListHeader = ({ house, status, message, handleSubmit }) => {
    return (
        <div className="game-list-header align-items-center">
            <Row>
                <Col className="d-flex py-1 px-lg-5 py-sm-3 align-items-center"><h1>{house.description}</h1></Col>
            </Row>
            <Row className="d-flex align-items-center justify-content-center">
                <Col lg={8} className="border-black rounded-3 bg-dark p-4 shadow-sm">
                    <CreateGameForm house={house} handleSubmit={handleSubmit} status={status} message={message} />
                </Col>
            </Row>
        </div>
    );
}

const GameList = ({ games }) => {
    const navigate = useNavigate();

    if (!Object.keys(games).length) {
        return (
            <Card className="p-3">
                <div className="text-muted align-self-center">No games yet!</div>
            </Card>
        );
    }

    return (
        <>
            <Row className="summary-header d-flex align-items-center mb-3">
                <Col xs={3} lg={4} className="align-self-center">
                    <span className="header-label">Game</span>
                </Col>
                <Col xs={3} lg={2} className="money">
                    <span className="header-label">Buy Ins</span>
                </Col>
                <Col xs={3} lg={2} className="money">
                    <span className="header-label">Cash Outs</span>
                </Col>
                <Col xs={3} lg={2} className="money">
                    <span className="header-label">Net</span>
                </Col>
                <Col md={2} className="d-flex justify-content-end">
                    <span className="header-label">Players</span>
                </Col>
            </Row>
            {Object.entries(games).map(([gameId, g]) =>
                <GameSummary 
                    key={gameId}
                    game={g}
                    navigate={navigate} 
                />
            )}
        </>
    );
}

const GameSummary = ({ game, navigate }) => {
    const { deleteGame } = useHouseDispatch();

    const formattedDate = formatDate(game.date);
    let [showDelete, setShowDelete] = useState(false);

    const onLongPress = () => {
        setShowDelete(!showDelete);
    }
    const onClick = () => {
        navigate(`/games/${game.id}`);
    };

    const longPressEventHandler = useLongPress({ onLongPress, onClick });
    const handleDelete = async () => {
        await deleteGame(game.id);
    }

    return (
        <Row className="position-relative mb-3 overflow-hidden" {...longPressEventHandler}>
            <div className="game-summary">
                <Row className="summary-content d-flex align-items-center">
                    <Col xs={3} lg={4} className="align-self-center">
                        <div className="game-info">
                            <span className="game-description">{game.description}</span>
                        </div>
                    </Col>
                    <Col xs={3} lg={2} className="money">{formatMoney(game.totalBuyIns)}</Col>
                    <Col xs={3} lg={2} className="money">{formatMoney(game.totalCashOuts)}</Col>
                    <Col xs={3} lg={2} className={`fw-bold ${game.totalNet >= 0 ? 'text-success' : 'text-danger'} money`}>{formatMoney(game.totalNet)}</Col>
                    <Col md={2} className="d-flex justify-content-end">
                        <Badge bg={"secondary"}><Person />&nbsp;{game.numPlayers || 0}</Badge>
                    </Col>
                </Row>
                <small className='game-date'>{formattedDate}</small>
            </div>
            <DeleteButton show={showDelete} handleDelete={handleDelete} shouldLimitWidth={true} />
        </Row>
    );
};

const CreateGameForm = ({ handleSubmit, status, message }) => {
    return (
        <Form onSubmit={handleSubmit}>
            <Stack gap={3}>
                <Row className="d-flex align-items-center justify-content-between">
                    <Col xs={8} className='d-sm-flex gap-3 justify-content-center'>
                        <div className='flex-grow-1' style={{ maxWidth: '20em' }}>
                            <Form.Control
                                required={true}
                                name="description"
                                placeholder="Description..."
                            />
                        </div>
                        <div className='mt-2 mt-sm-0' style={{ maxWidth: '20em' }}>
                            <Form.Control
                                required={true}
                                name="date"
                                type="date"
                                placeholder="Date..."
                            />
                        </div>
                    </Col>
                    <Col xs={4} className="d-flex justify-content-center">
                        <div className="vr me-4 me-sm-5"></div>
                        <Button
                            type="submit"
                            variant="outline-success"
                        >
                            Create Game
                        </Button>
                    </Col>
                </Row>
                {status && status !== 'success' && (
                    <Row>
                        <Col xs={12}>
                            <span>{status}: {message}</span>
                        </Col>
                    </Row>
                )}
            </Stack>
        </Form>
    )
}

export default House;
